import { autoinject, transient, observable, computedFrom, BindingEngine } from "aurelia-framework";
import { DataType, QueryFilter, DataGridColumn, SourceType } from "../../../../../models/queries/queries.models";
import { I18N } from "aurelia-i18n";
import * as moment from "moment";
import { LookupValue, LookupValueRequest, LookupValueResponse } from "../../../../../models/lookups/lookup.models";
import { MobileHelper } from "../../../../../helpers/mobile-helper";
import { LookupValueQuery } from "../../../../../constants/modules/lookup.constants";
import { LookupClientService } from "../../../../../services/lookups/lookup-client.service";
import * as Enumerable from "linq";
import { Router } from "aurelia-router";
import { BaseActivity } from "../base-activity";
import { TasksConstants } from "../../../../../models/tasks/task.model";
import KeyDownEvent = JQuery.KeyDownEvent;
import { Guid } from "../../../../../utilities/guid";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../constants/authentication/claims";
import { Claims as CommonUiClaims } from "@rightship-tech/common-ui/constants/authentication/claims";
import { UserClientService } from "../../../../../services/authentication/user-client.service";
import { WorkflowStateEnum } from "../../../../../models/workflow/workflow.models";
import { QueriesClientService } from "../../../../../services/queries/queries-client.services";
import { FormsStatusEnum } from "../../../../../models/vetting/vetting.models";
import { BindingSignaler } from "aurelia-templating-resources";
import { SignalFunctions } from "../../../../../services/basics/signal-functions";
import { SignalClientService } from '@rightship-tech/common-ui/services/basic/signal-client.service';
import { RightshipDataGridColumn } from "../../../../components/rightship-data-grid/rightship-data-grid";
import { cachable } from '@rightship-tech/common-ui/decorators/cachable';
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { EntityTypeConfigurationModel } from "@rightship-tech/common-ui/components/comp-entity-finder/comp-entity-finder.models";
import { EntityTypeConfigurationHelper } from "@rightship-tech/common-ui/helpers/entity-type-configuration.helper";
import { Reflection } from "@rightship-tech/common-ui/helpers/reflection";

@autoinject()
@transient()
export class FormsActivities extends BaseActivity {

    private _isReloading: boolean;

    public companiesEntityTypes: EntityTypeConfigurationModel[];
    @cachable
    selectedFilter: string[] = [];
    @cachable
    selectedLookupFilter: { title: string, clear: () => void, apply: () => void };
    statuses: LookupValue[];
    isMobileFilterOpen: boolean;

    public defaultPageCount: number = 100;
    @cachable
    public rightshipMember: boolean = false;
    public myFormsCount: number = 0;
    public pendingFormInputCount: number;
    public pendingTerminalReviewCount: number;
    public pendingRightshipReviewCount: number;
    public acceptableCount: number;
    public unacceptableCount: number;
    public cancelledCount: number;
    public acceptableDayOnlyCount: number;
    public completedCount: number;
    public accessibleFormIds: number[] = [];
    public isColumnFiltering: boolean = false;

    public getItemRoute = (id) => `/vet-task-onboarding-custom-content?vesselApplicationFormId=${id}`;
    public initialFilters: QueryFilter[];

    public onboardingConfigs: { id: number, name: string, onboardingVersionCategoryId?: number }[] = [];

    private accessableForms: { id: number, name: string, onboardingVersionCategoryId?: number }[] = [];

    isRightShipUser: boolean;

    protected get odataUrl() {
        return "odata/VetTaskActivities";
    }

    getStatusFilters() {
        return FormsActivities.formsStatuses.filter(x => x.name != "Computing Rules");
    }

    @observable()
    @cachable
    filter: string;
    @cachable
    appliedFilter: string;

    filterKeyDown($event) {
        const e: KeyDownEvent = $event.detail;

        if (e.key == "Enter") {
            this.appliedFilter = this.filter;
            this.reloadItemSource();
        }
    }

    filterChanged() {
        if ((this.filter && this.filter.length >= 5) || this.filter == "") {
            this.appliedFilter = this.filter;
            this.reloadItemSource();
        }
    }

    @cachable
    public myFormsFilterSelected: boolean = false;
    myFormsClick(isMobile: boolean = false) {
        this.myFormsFilterSelected = !this.myFormsFilterSelected;
        if (!isMobile) this.reloadItemSource();
    }

    clearFilter() {
        this.filter = "";
        this.onboardingConfigFilterIds = [];
        this.selectedDocCompanies = [];
        this.dwtFirstFilter = null;
        this.dwtSecondFilter = null;
        this.dwtIsApplied = false;
        this.appliedFilter = this.filter;
        this.reloadItemSource();
    }

    // the reason we have this is to override the status with status that we want for Forms
    public static formsStatuses: { name: string, internalName: string, workflowStatusId?: number }[] = [
        {
            name: FormsStatusEnum.ComputingRules,
            internalName: "onboarding-rules-check-activity"
        }, {
            name: FormsStatusEnum.PendingFormInput,
            internalName: "onboarding-submitted-activity"
        }, {
            name: FormsStatusEnum.PendingTerminalReview,
            internalName: "terminal-review-activity"
        }, {
            name: FormsStatusEnum.PendingRightshipReview,
            internalName: "rightship-review-activity"
        }, {
            name: FormsStatusEnum.Acceptable,
            internalName: null,
            workflowStatusId: 3
        }, {
            name: FormsStatusEnum.Unacceptable,
            internalName: null,
            workflowStatusId: 4
        }, {
            name: FormsStatusEnum.Cancelled,
            internalName: null,
            workflowStatusId: 5
        }, {
            name: FormsStatusEnum.AcceptableDayOnly,
            internalName: null,
            workflowStatusId: 1000
        }, {
            name: FormsStatusEnum.Completed,
            internalName: null,
            workflowStatusId: 1001
        }];

    constructor(i18N: I18N, protected bindingEngine: BindingEngine, private signalClientService: SignalClientService, private lookupClientService: LookupClientService,
        router: Router, private userClaimsService: UserClaimsService, private userClientService: UserClientService, private queryService: QueriesClientService,
        private bindingSignaler: BindingSignaler, private entityTypeConfigurationHelper: EntityTypeConfigurationHelper) {
        super(i18N, router, TasksConstants.FormsListName, bindingEngine);
        this.companiesEntityTypes = this.entityTypeConfigurationHelper.getConfigurationByKey(["BasicCompanySearch"]);
    }

    async init() {
        this.isRightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.canAccessDueDiligence = this.userClaimsService.hasPermission(CommonUiClaims.CanAccessDueDiligence);

        const promises = [];
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.WorkflowStatuses;
        promises.push(this.lookupClientService.getLookup(request));

        const results = await Promise.all(promises);
        this.statuses = Enumerable.from((<LookupValueResponse>results[0]).activeValues).orderBy(x => x.id).toArray();
    }

    async activate(params: any) {
        await this.init();
        super.activate(params);
        this.columns = [];
        this.columns.push(
            <DataGridColumn>{
                columnOrder: -1,
                sortOrder: -1,
                dataType: DataType.Number,
                name: 'workflowStatusId',
                isHidden: true
            },
            <DataGridColumn>
            {
                dataType: DataType.CustomTemplate,
                value:
                    '<div class="column">' +
                    '<div class="row align-items-middle">' +
                    '<a class="link" href.bind="getItemRoute(item)" mousedown.trigger="itemClick()">${item.vesselName}</a>' +
                    '<svg class="rs-badge" viewBox="0 0 30 30" if.bind="item.isRightShipMember && isRightShipUser">' +
                    '<g>' +
                    '<path fill="#003745" d="M2,0h26c1.1,0,2,0.9,2,2v26c0,1.1-0.9,2-2,2H2c-1.1,0-2-0.9-2-2V2C0,0.9,0.9,0,2,0z"/>' +
                    '<path fill="#FF5F1B" d="M2,0h1.5c0.5,0,1,0.2,1.4,0.6l15.5,14.8l0,0L4.9,29.5C4.5,29.8,4,30,3.5,30H2c-1.1,0-2-0.9-2-2V2C0,0.9,0.9,0,2,0z"/>' +
                    '</g>' +
                    '</svg>' +
                    '</div>' +
                    '<span if.bind="item.internationalMaritimeOrganizationNumber">IMO ${item.internationalMaritimeOrganizationNumber}</span>' +
                    '<span if.bind="item.vehicleIdentificationNumber">VIN ${item.vehicleIdentificationNumber}</span>' +
                    '<span if.bind="item.europeanNumberOfIdentification">ENI ${item.europeanNumberOfIdentification}</span>' +
                    '</div>',
                name: "vesselName",
                title: "Name",
                instancePerRow: true,
                sortOrder: 1,
                columnOrder: -1,
                isSortable: true,
                parameter: {
                    item: null,
                    getItemRoute: this.getRoute.bind(this),
                    itemClick: this.itemClick.bind(this),
                    isRightShipUser: this.isRightShipUser
                }
            },
            <DataGridColumn>{
                columnOrder: 1,
                sortOrder: 5,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'formName',
                title: 'Form',
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "200px",
                value:
                    '<div class="form" if.bind="item.formName">' +
                    '<span class="name">${item.formName}</span>' +
                    '<span class="vet" if.bind="item.vetId">VET ID ${item.vetId}</span>' +
                    '<span class="vet" if.bind="!item.vetId">FORM ID ${item.vesselApplicationFormId}</span>' +
                    '</div>',
                isSortable: true,
                parameter: {
                    item: null,
                }
            },
            <DataGridColumn>{
                columnOrder: 2,
                sortOrder: 7,
                dataType: DataType.DateTime,
                isSortable: true,
                name: 'submittedDate',
                title: 'Submitted Date',
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "150px",
                valueConverter: (date: Date | string) => date ? moment(date).format('DD MMM YYYY HH:mm') : '',
            },
            <RightshipDataGridColumn>{
                columnOrder: 3,
                sortOrder: 3,
                dataType: DataType.Default,
                isSortable: true,
                name: 'ruleName',
                title: 'Rule Triggered',
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "250px",
                ellipsis: 75,
                isHidden: this.params.isPortsAndTerminals
            },
            <DataGridColumn>{
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.Default,
                isSortable: true,
                name: 'businessUnitName',
                title: 'Business Unit',
                isHidden: this.params.isPortsAndTerminals
            },
            <DataGridColumn>{
                columnOrder: 6,
                sortOrder: 6,
                dataType: DataType.DateTime,
                isSortable: false,
                name: 'validityExpiryDate',
                title: 'Valid Until',
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "110px",
                valueConverter: (date: Date | string) => date ? moment(date).format('DD MMM YYYY') : '- -',
            },
            <DataGridColumn>{
                columnOrder: 7,
                sortOrder: 1,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                isSortable: true,
                isFilterable: true,
                name: 'workflowStatusNotes',
                title: 'Status',
                value:
                    '<div class="status-badge-value">' +
                    '   <div class="row nowrap align-items-middle">' +
                    '       <div class="status-badge-dot"></div>' +
                    '       <span>${getStatus(item)}</span>' +
                    '   </div>' +
                    '   <span class="status-date">${getDateToString(item.statusDate)}</span>' +
                    '</div>',
                getCellClass: (item: { workflowStatusNotes: string }) => {
                    const status = item.workflowStatusNotes;
                    return status ? `status-badge ${status.toLowerCase().replace(/[^A-z0-9]+/g, '-')}` : '';
                },
                parameter: { // the reason we have this is to override the status with status that we want for Forms
                    item: null,
                    getStatus: (item: any) => {
                        return item.workflowStatusNotes;
                    },
                    getDateToString: (date: Date | string) => this.getDateToString(date)
                }
            });

        await this.loadAccessibleForms();

        this.initialSorting = this.getSorting();

        if (this.applyCache()) {
            const clonedSelectedFilter = CloneHelper.cloneObject(this.selectedFilter);
            //Reset the selectedFilter so that filterClick will work properly
            this.selectedFilter = [];
            clonedSelectedFilter.forEach(filter => {
                const status = FormsActivities.formsStatuses.filter(x => x.name == filter)[0];
                this.filterClick(status, false);
            });
            this.reloadItemSource();
        } else {
            this.setDefaultFilters();
        }
    }

    attached() {
        this.signalClientService.subscribe(SignalFunctions.formStatusChanged, this.refreshFormStatus);
    }

    detached() {
        this.signalClientService.unsubscribe(SignalFunctions.formStatusChanged, this.refreshFormStatus);
        super.detached();
    }


    refreshFormStatus = async (param) => {
        if (this._isReloading) {
            console.log('refreshFormStatus is already in progress, skipping...');
            return;
        }

        try {
            if (this.dataGridVM) {
                this._isReloading = true;
                if (this.dataGridVM.page == 0) {
                    await this.dataGridVM.reloadData();
                } else {
                    const item = this.dataGridVM.itemsCollection.filter(x => x.item.vesselApplicationFormId == param.entityId)[0];
                    if (item) {
                        item.item.status = FormsActivities.formsStatuses.filter(x => x.name == param.statusNotes).map(x => x.name)[0];
                    }
                }
                await this.refreshActivityCounts();
            }
        }
        finally {
            this._isReloading = false;
        }
    }

    protected setDefaultFilters() {
        if (!this.params?.noDefaultFilters) {
            if (!this.isRightShipUser) {
                const review = FormsActivities.formsStatuses.filter(x => x.internalName == 'terminal-review-activity')[0];
                this.filterClick(review);
            }
            else {
                const review = FormsActivities.formsStatuses.filter(x => x.internalName == 'rightship-review-activity')[0];
                this.filterClick(review);
            }
        }
        if (MobileHelper.isMobileDevice()) {
            this.setFiltersApplied();
        }

        this.reloadItemSource();
    }

    getRoute(item) {
        return this.getItemRoute(item.vesselApplicationFormId);
    }

    getCount(statusName: string) {
        if (statusName == FormsStatusEnum.PendingFormInput && this.pendingFormInputCount)
            return this.pendingFormInputCount;
        if (statusName == FormsStatusEnum.PendingTerminalReview && this.pendingTerminalReviewCount)
            return this.pendingTerminalReviewCount;
        if (statusName == FormsStatusEnum.PendingRightshipReview && this.pendingRightshipReviewCount)
            return this.pendingRightshipReviewCount;
        if (statusName == FormsStatusEnum.Acceptable && this.acceptableCount)
            return this.acceptableCount;
        if (statusName == FormsStatusEnum.Unacceptable && this.unacceptableCount)
            return this.unacceptableCount;
        if (statusName == FormsStatusEnum.Cancelled && this.cancelledCount)
            return this.cancelledCount;
        if (statusName == FormsStatusEnum.AcceptableDayOnly && this.acceptableDayOnlyCount)
            return this.acceptableDayOnlyCount;
        if (statusName == FormsStatusEnum.Completed && this.completedCount)
            return this.completedCount;

        return 0;
    }

    rightshipMemberClick(isMobile: boolean = false) {
        this.rightshipMember = !this.rightshipMember;
        if (!isMobile) this.reloadItemSource();
    }

    reloadItemSource() {
        const initialFilters = [];

        if (this.myFormsFilterSelected) {
            initialFilters.push(QueryFilter.getQuery("actionTakerId", this.userClientService.currentUser.id, "eq", DataType.Number, true));

            initialFilters.push(QueryFilter.getQuery("workflowStatusId", [
                WorkflowStateEnum.inProgress, WorkflowStateEnum.pending
            ], "{0} in ({1})", DataType.Number, true, false, null, false, true));
        }

        if (this.appliedFilter) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("vesselName", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
            initialFilters.push(QueryFilter.getQuery("internationalMaritimeOrganizationNumber", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            initialFilters.push(QueryFilter.getQuery("vetIdStr", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
        }

        if (this.rightshipMember) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("isRightShipMember", true, "eq", DataType.Boolean, true, false, guid, true));
        }

        if (this.onboardingConfigFilterIds && this.onboardingConfigFilterIds.length) {

            let onboardingConfigIds: number[] = [];

            this.onboardingConfigFilterIds.forEach(selectedOnboardingConfigId => {
                const item = this.onboardingConfigs.filter(x => x.id == selectedOnboardingConfigId)[0];

                if (item.onboardingVersionCategoryId) {
                    const linkedOnboardingConfigIds = this.accessableForms.filter(x => x.onboardingVersionCategoryId == item.onboardingVersionCategoryId).map(x => x.id);
                    onboardingConfigIds = onboardingConfigIds.concat(linkedOnboardingConfigIds);
                }
                else {
                    onboardingConfigIds.push(selectedOnboardingConfigId);
                }
            });

            const guid = Guid.generateGuid();
            const query = QueryFilter.getQuery("onboardingConfigId", onboardingConfigIds, "{0} in ({1})", DataType.Number, true, false, guid, true);
            query.filterOperator.requiresInterpolation = true;

            initialFilters.push(query);
        }

        if (this.params) {
            const guid = Guid.generateGuid();
            if (this.params.vesselBKey) {
                initialFilters.push(QueryFilter.getQuery("vesselBKey", this.params.vesselBKey.toString(), "{0} eq '{1}'", DataType.Default, true, false, guid, true, true));
            }

            if (this.params.externalSource) {
                initialFilters.push(QueryFilter.getQuery("externalSource", this.params.externalSource, "{0} eq '{1}'", DataType.Default, true, false, guid, true, true));
            }

            if (this.params.businessUnitId) {
                initialFilters.push(QueryFilter.getQuery("businessUnitId", this.params.businessUnitId, "eq", DataType.Number, true, false, guid, true, false));
            }
        }
        
        //#region doc company
        if (this.docCompanyFilterActive) {

            const docCompanyFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "docCompanyId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedDocCompanies || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            initialFilters.push(docCompanyFilter);
        }
        //#endregion

        //#region dwt
        if (this.dwtFirstFilter || this.dwtSecondFilter) {
            initialFilters.push(QueryFilter.getQuery('dwt', this.dwtFirstFilter ? this.dwtFirstFilter : 0, 'ge', DataType.Number, true));
            initialFilters.push(QueryFilter.getQuery('dwt', this.dwtSecondFilter ? this.dwtSecondFilter : 0, 'le', DataType.Number, true));
        }
        //#endregion

        if (this.isPendingDueDiligence && this.dueDiligenceFilterVisible) {
            const guid = Guid.generateGuid();
            initialFilters.push(QueryFilter.getQuery("hasDueDiligence", this.isPendingDueDiligence, "eq", DataType.Boolean, true, false, guid, true));
        }

        this.initialFilters = initialFilters;
        this.itemSource = {
            sourceType: SourceType.ODataUrl,
            source: this.odataUrl,
            columns: this.columns,
            initialFilters: initialFilters,
            initialSorting: this.getSorting()
        };
        this.refreshActivityCounts();
    }

    public getSorting() {
        const columns: DataGridColumn[] = [];

        columns.push(
            <DataGridColumn>{
                columnOrder: -1,
                sortOrder: 1,
                dataType: DataType.DateTime,
                isSortable: true,
                isAscending: this.isRightShipUser,
                isHidden: true,
                name: 'submittedDate',
                title: 'SubmittedDate'
            });
        return columns;
    }
    
    clearAllFilters(reload: boolean = true) {
        if (this.dataGridVM) {
            this.dataGridVM.clearSorting();
            this.isColumnFiltering = false
        }
        if (this.columns.length > 0) {
            const workflowColumn = this.columns.filter(x => x.name == 'workflowStatusNotes')[0];

            workflowColumn.filters = [];

            this.selectedFilter.splice(0, this.selectedFilter.length);
            this.onboardingConfigFilterIds = [];
            this.selectedDocCompanies = [];
            this.dwtFirstFilter = null;
            this.dwtSecondFilter = null;
            this.dwtIsApplied = false;
            this.isPendingDueDiligence = false;

            this.rightshipMember = false;

            if (reload) {
                this.reloadItemSource();
            }
        }
    }

    filterClick(status: { name: string, internalName: string, workflowStatusId?: number }, reload: boolean = true) {

        if (this.columns.length > 0) {
            const workflowColumn = this.columns.filter(x => x.name == 'workflowStatusNotes')[0];
            if (!workflowColumn.filters)
                workflowColumn.filters = [];

            if (status) {
                const index = this.selectedFilter.indexOf(status.name);
                if (status.name && index == -1) {
                    workflowColumn.filters.push(QueryFilter.getQuery("workflowStatusNotes", status.name, "eq", DataType.Default));
                    this.selectedFilter.push(status.name);
                } else {
                    const filterIndex = workflowColumn.filters.findIndex(x => x.filterValue == status.name);
                    workflowColumn.filters.splice(filterIndex, 1);
                    this.selectedFilter.splice(index, 1);
                }
            }

            if (this.dataGridVM && reload) {
                this.dataGridVM.reloadData();
            }
        }
    }

    async loadAccessibleForms() {
        //Get all forms this user has
        const forms = await this.queryService.getData(`${this.odataUrl}/Genesis.GetAccessableForms()`);
        const accessableFormsIds = forms.value.map(x => x.id);
        const accessableFormsVersionCategoryIds = forms.value.filter(y => y.onboardingVersionCategoryId != null).map(x => x.onboardingVersionCategoryId);

        //Lets load the all the forms
        const request = new LookupValueRequest();
        request.lookupValueQuery = 'OnboardingConfigs';

        const lookupOnboardingConfig = await this.lookupClientService.getLookup(request);
        //filter the lookups based on what is accessible         
        const lookupOnboardingConfigs = lookupOnboardingConfig.activeValues.filter(x => accessableFormsIds.indexOf(x.id) > -1 ||
            accessableFormsVersionCategoryIds.indexOf(x.additionalData.onboardingVersionCategoryId) > -1);

        //set the accessible forms array based on that information
        this.accessableForms = forms.value.filter(y => lookupOnboardingConfigs.filter(z => z.id == y.id).length > 0);

        this.onboardingConfigs = [];

        //populate onboardingConfigs and group them manually by onboardingVersionCategory
        Enumerable.from(this.accessableForms)
        .orderByDescending(x => x.id)
        .forEach(accessableForm => {

            const lookupOnboardingConfig = lookupOnboardingConfigs.filter(x => x.id == accessableForm.id)[0];

            if (!lookupOnboardingConfig.additionalData.onboardingVersionCategoryId) {
                this.onboardingConfigs.push({ id: lookupOnboardingConfig.id, name: lookupOnboardingConfig.name, onboardingVersionCategoryId: lookupOnboardingConfig.additionalData.onboardingVersionCategoryId });
            }
            else {
                if (this.onboardingConfigs.filter(x => x.onboardingVersionCategoryId == lookupOnboardingConfig.additionalData.onboardingVersionCategoryId).length == 0) {
                    this.onboardingConfigs.push({ id: lookupOnboardingConfig.id, name: lookupOnboardingConfig.additionalData.onboardingVersionCategory, onboardingVersionCategoryId: lookupOnboardingConfig.additionalData.onboardingVersionCategoryId });
                }
            }
        });
    }

    async refreshActivityCounts() {
        if (this.userClaimsService.hasPermission(Claims.CanAccessForms)) {
            const url = `${this.odataUrl}/Genesis.GetStatusCounts(vesselBKey='${this.params.vesselBKey || ''}', externalSource='${this.params.externalSource || ''}', businessUnitId=${this.params.businessUnitId || null})`;
            const x = await this.queryService.getData(url);

            this.clearCounts();

            for (let i = 0; i < x.keys.length; ++i) {
                const key = x.keys[i];
                const value = x.values[i];
                if (key == FormsStatusEnum.PendingFormInput) {
                    this.pendingFormInputCount = value;
                } else if (key == FormsStatusEnum.PendingTerminalReview) {
                    this.pendingTerminalReviewCount = value;
                } else if (key == FormsStatusEnum.PendingRightshipReview) {
                    this.pendingRightshipReviewCount = value;
                } else if (key == FormsStatusEnum.Acceptable) {
                    this.acceptableCount = value;
                } else if (key == FormsStatusEnum.Unacceptable) {
                    this.unacceptableCount = value
                } else if (key == FormsStatusEnum.Cancelled) {
                    this.cancelledCount = value;
                } else if (key == FormsStatusEnum.AcceptableDayOnly) {
                    this.acceptableDayOnlyCount = value;
                } else if (key == FormsStatusEnum.Completed) {
                    this.completedCount = value;
                } else if (key == "myForms") {
                    this.myFormsCount = value;
                } else if (key == "pendingDueDiligence") {
                    this.pendingDueDiligenceCount = value;
                }
            }

            this.bindingSignaler.signal('counts-updated');
        }
    }

    clearCounts() {
        this.pendingFormInputCount = 0;
        this.pendingTerminalReviewCount = 0;
        this.pendingRightshipReviewCount = 0;
        this.acceptableCount = 0;
        this.unacceptableCount = 0;
        this.cancelledCount = 0;
        this.acceptableDayOnlyCount = 0;
        this.completedCount = 0;
        this.myFormsCount = 0;
        this.pendingDueDiligenceCount = 0;
    }

    private removeNotAppliedFilters() {
        const workflowColumn = this.columns.filter(x => x.name == 'workflowStatusNotes')[0];
        if (workflowColumn && workflowColumn.filters && workflowColumn.filters.length > 0) {
            const indexes = [];
            workflowColumn.filters.forEach((f, i) => {
                if (f["isApplied"] == true)
                    return;
                const index = this.selectedFilter.indexOf(f.filterValue);
                this.selectedFilter.splice(index, 1);
                indexes.push(i);
            });
            indexes.forEach(i => {
                workflowColumn.filters.splice(i, 1);
            });

            if (this.dataGridVM && indexes.length > 0)
                this.dataGridVM.reloadData();
        }
    }

    mobileFilterApply() {
        this.setFiltersApplied();
        this.reloadItemSource();
        this.toggleMobileFilterDialog();
    }

    private setFiltersApplied() {
        const workflowColumn = this.columns.filter(x => x.name == 'workflowStatusNotes')[0];
        if (workflowColumn && workflowColumn.filters && workflowColumn.filters.length > 0) {
            workflowColumn.filters.forEach(f => {
                f["isApplied"] = true;
            });
        }
    }

    public closeAllFilters() {
        this.showOnboardingConfigFilter = false;
        this.showDocCompanyFilter = false;
        this.showDwtFilter = false;
        return false;
    }

    //#region onboarding config filter
    public showOnboardingConfigFilter: boolean = false;
    @cachable
    public onboardingConfigFilterIds: number[] = [];

    toggleOnboardingConfigFilters() {
        if (!this.showOnboardingConfigFilter) {
            this.closeAllFilters();
        }
        this.showOnboardingConfigFilter = !this.showOnboardingConfigFilter;
        this.selectedLookupFilter = {
            title: "Forms",
            clear: () => {
                this.onboardingConfigFilterIds = [];
                this.reloadItemSource();
            },
            apply: () => {
                this.reloadItemSource();
                this.showOnboardingConfigFilter = false;
            }
        }
    }

    onOnboardingConfigFilter(event, reload: boolean = true) {
        this.onboardingConfigFilterIds = event.filters.map(x => x.id);
        if (reload == true) this.reloadItemSource();
        this.showOnboardingConfigFilter = false;
    }

    @computedFrom('onboardingConfigFilterIds', 'onboardingConfigFilterIds.length')
    public get onboardingConfigFilterCount() {
        return this.onboardingConfigFilterIds ? this.onboardingConfigFilterIds.length : 0;
    }

    public clearOnboardingConfigFilter() {
        this.onboardingConfigFilterIds = [];
        this.reloadItemSource();
    }

    //#endregion

    public get mobileFilterSelected() {
        return this.showColumnSorting;
    }

    public get isSubFilterOpen(): boolean {
        return this.showColumnSorting;
    }

    toggleMobileFilterDialog() {
        if (!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }

        if (!this.isMobileFilterOpen)
            this.removeNotAppliedFilters();

        this.showColumnSorting = false;
    }

    hideMobileFilters() {
        this.isMobileFilterOpen = false;
    }

    public mobileApplySelectedFilter() {
        if (this.selectedFilter) {
            this.setFiltersApplied();
        }

        this.showColumnSorting = false;
    }


    //#region column sorting 

    public showColumnSorting: boolean = false;
    public selectedColumnSorting: number[] = [];

    public toggleColumnSortingFilters() {
        if (!this.showColumnSorting) {
            this.closeAllFilters();
        }
        this.showColumnSorting = !this.showColumnSorting;
        // this.selectedFilter = {
        //     title: "Column sorting",
        //     clear: () => {
        //         this.clearBUFilter();
        //     },
        //     apply: () => {
        //         this.applyColumnSortingFilter(this.selectedColumnSorting.map(bId => {
        //             return { id: bId };
        //         }), false);
        //     }
        // }
    }

    public applyColumnSortingFilter(items: any[], reload: boolean = true) {
        this.selectedColumnSorting = items.map(x => x.id);
        if (reload == true) this.reloadItemSource();
        this.toggleColumnSortingFilters();
    }

    sortByMobile(column: DataGridColumn) {
        this.dataGridVM.sortingTriggered(column);
        this.isColumnFiltering = this.dataGridVM.visibleColumns.some(item => item.isAscending === true || item.isAscending === false);
        this.reloadItemSource();
    }

    sortClass(column: DataGridColumn) {
        if (column.isAscending) {
            return "Ascending"
        }
        else if (column.isAscending == false) {
            return "Descending"
        }
        else {
            return "";
        }
    }

    //#endregion

    //#region doc company filter
    public showDocCompanyFilter: boolean = false;
    @cachable
    public selectedDocCompanies: number[] = [];

    public toggleDocCompanyFilters() {
        if (!this.showDocCompanyFilter) {
            this.closeAllFilters();
        }
        this.showDocCompanyFilter = !this.showDocCompanyFilter;
    }

    public applyDocCompanyFilter(items: any[], reload: boolean = true) {
        if (!items) {
            return;
        }
        this.selectedDocCompanies = items.map(x => x.id);
        if (reload == true) this.reloadItemSource();
        this.toggleDocCompanyFilters();
    }

    public clearDocCompanyFilter() {
        this.selectedDocCompanies = [];
        this.reloadItemSource();
    }

    @computedFrom("selectedDocCompanies", "selectedDocCompanies.length")
    public get docCompanyFilterActive() {
        return this.selectedDocCompanies?.length > 0 && this.selectedDocCompanies[0] != null;
    }

    //#endregion

    //#region dwt filter
    @cachable
    public dwtFirstFilter: number;
    @cachable
    public dwtSecondFilter: number;
    public dwtDefaults: { low: number, high: number } = { low: 0, high: 565000 };
    public showDwtFilter: boolean = false;
    public dwtIsApplied: boolean = false;

    public toggleDwtFilters() {
        if (!this.showDwtFilter) {
            this.closeAllFilters();
        }
        this.showDwtFilter = !this.showDwtFilter;
    }

    public applyDwtFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.dwtFirstFilter = filterValue.low;
            this.dwtSecondFilter = filterValue.high;
            this.dwtIsApplied = true;
        }

        this.reloadItemSource();
        this.toggleDwtFilters();
    }

    public clearDwtFilter() {
        this.dwtFirstFilter = null;
        this.dwtSecondFilter = null;
        this.dwtIsApplied = false;
        this.reloadItemSource();
    }

    @computedFrom("dwtIsApplied", "dwtFirstFilter", "dwtSecondFilter")
    public get dwtFilterActive() {
        return this.dwtIsApplied && ((this.dwtFirstFilter && this.dwtFirstFilter >= this.dwtDefaults.low) || (this.dwtSecondFilter && this.dwtSecondFilter <= this.dwtDefaults.high));
    }

    //#endregion

    //#region due diligence filter
    
    public pendingDueDiligenceCount: number = 0;
    public canAccessDueDiligence: boolean;
    @cachable
    public isPendingDueDiligence: boolean;
    
    @computedFrom(Reflection.getPropertyPath<FormsActivities>(z => z.canAccessDueDiligence),
        Reflection.getPropertyPath<FormsActivities>(z => z.isRightShipUser))
    public get dueDiligenceFilterVisible(): boolean
    {
        return this.canAccessDueDiligence && this.isRightShipUser;
    }

    pendingDueDiligenceClick(isMobile: boolean = false) {
        this.isPendingDueDiligence = !this.isPendingDueDiligence;
        if (!isMobile) this.reloadItemSource();
    }

    //#endregion
}
